// CFOLiveStrip — Continuous Truth surface on every authenticated page.
//
// Polls /api/cfo-signals/status every 45 seconds and renders a slim
// horizontal strip with a colour code (green/yellow/red), the current
// headline, and a click-through to the deep-dive drawer.
//
// Design rules (per architecture review):
//   • Always-on but UNINTRUSIVE — green state is a 4-pixel-tall hairline
//     showing only "Live" + dot. Yellow/red expand to a one-line strip
//     with the headline + actionable counts.
//   • One round-trip per tick. No websocket / SSE infra dependency.
//   • Best-effort: fetch failures render NOTHING (never block UI).
//   • The drawer that opens on click is a transient overlay; it
//     does NOT add a new top-level route.
import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { Activity, AlertTriangle, AlertOctagon, ChevronRight, X } from 'lucide-react';

const POLL_MS = 45_000;

function colourFor(level) {
  switch (level) {
    case 'red':    return {
      bar:  'bg-rose-600',
      text: 'text-white',
      dot:  'bg-rose-200',
      Icon: AlertOctagon,
    };
    case 'yellow': return {
      bar:  'bg-amber-500',
      text: 'text-white',
      dot:  'bg-amber-100',
      Icon: AlertTriangle,
    };
    default: return {
      bar:  'bg-emerald-50 border-emerald-200 border-b',
      text: 'text-emerald-700',
      dot:  'bg-emerald-500',
      Icon: Activity,
    };
  }
}

function formatTime(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleTimeString(); } catch (_) { return ''; }
}

export default function CFOLiveStrip() {
  const [status, setStatus] = useState(null);
  const [error, setError]   = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/cfo-signals/status?window_hours=24');
      setStatus(res.data);
      setError(false);
    } catch (_) {
      setError(true);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(t);
  }, [fetchStatus]);

  if (error || !status) return null;

  const { level, headline, counts } = status;
  const c = colourFor(level);
  const Icon = c.Icon;

  // GREEN — hairline only. No headline text to avoid noise.
  if (level === 'green') {
    return (
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        data-testid="cfo-live-strip"
        data-cfo-level={level}
        className={`w-full ${c.bar} py-1 px-4 flex items-center justify-between text-[10px] uppercase tracking-wider ${c.text} hover:bg-emerald-100 transition-colors`}
      >
        <span className="flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${c.dot} animate-pulse`} />
          <span className="font-semibold">CFO Live</span>
          <span className="opacity-70 normal-case tracking-normal">{headline}</span>
        </span>
        <span className="opacity-60">Last check {formatTime(status.evaluated_at)}</span>
        {drawerOpen && (
          <SignalDeepDive
            initialStatus={status}
            onClose={(e) => { e.stopPropagation(); setDrawerOpen(false); }}
          />
        )}
      </button>
    );
  }

  // YELLOW / RED — full one-line strip with headline + counts.
  return (
    <>
      <div
        data-testid="cfo-live-strip"
        data-cfo-level={level}
        className={`w-full ${c.bar} ${c.text} px-4 py-1.5 flex items-center justify-between gap-3 text-xs cursor-pointer`}
        onClick={() => setDrawerOpen(true)}
      >
        <span className="flex items-center gap-3 min-w-0">
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span className="font-semibold">CFO Live · {level.toUpperCase()}</span>
          <span className="opacity-95 truncate">{headline}</span>
        </span>
        <span className="flex items-center gap-3 flex-shrink-0">
          {counts.critical > 0 && (
            <span className="px-1.5 py-0.5 bg-rose-900/30 rounded">
              {counts.critical} critical
            </span>
          )}
          {counts.high > 0 && (
            <span className="px-1.5 py-0.5 bg-black/15 rounded">
              {counts.high} high
            </span>
          )}
          {counts.medium > 0 && (
            <span className="px-1.5 py-0.5 bg-black/10 rounded">
              {counts.medium} medium
            </span>
          )}
          <ChevronRight className="w-3.5 h-3.5" />
        </span>
      </div>
      {drawerOpen && (
        <SignalDeepDive
          initialStatus={status}
          onClose={() => setDrawerOpen(false)}
        />
      )}
    </>
  );
}


// ───────────────────────────────────────────────────────────────────────
// SignalDeepDive — slide-out drawer with the recent stream + filters.
// ───────────────────────────────────────────────────────────────────────
function SignalDeepDive({ initialStatus, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minSeverity, setMinSeverity] = useState('all');

  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (minSeverity !== 'all') params.min_severity = minSeverity;
      const res = await axiosInstance.get('/cfo-signals/recent', { params });
      setRows(res.data || []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [minSeverity]);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  const sevPill = (s) => {
    const map = {
      info:     'bg-slate-100 text-slate-700',
      low:      'bg-sky-100 text-sky-700',
      medium:   'bg-amber-100 text-amber-700',
      high:     'bg-orange-100 text-orange-700',
      critical: 'bg-rose-100 text-rose-700',
    };
    return `text-[10px] uppercase px-1.5 py-0.5 rounded ${map[s] || map.info}`;
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end"
      data-testid="cfo-signals-drawer"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" />
      <aside
        className="relative w-full max-w-xl h-full bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">CFO Signal Stream</h2>
            <p className="text-[11px] text-slate-500">
              {initialStatus?.headline} · window {initialStatus?.window_hours}h
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
            data-testid="cfo-drawer-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/60">
          <div className="flex flex-wrap gap-1 text-[11px]">
            {['all', 'medium', 'high', 'critical'].map((s) => (
              <button
                key={s}
                onClick={() => setMinSeverity(s)}
                className={`px-2 py-1 rounded ${
                  minSeverity === s
                    ? 'bg-purple-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`cfo-filter-${s}`}
              >
                {s === 'all' ? 'All' : `≥ ${s}`}
              </button>
            ))}
          </div>
        </div>

        <div className="px-5 py-3 space-y-2 text-xs">
          {loading && <p className="text-slate-500">Loading…</p>}
          {!loading && rows.length === 0 && (
            <p className="text-slate-500" data-testid="cfo-drawer-empty">
              لا توجد إشارات مطابقة.
            </p>
          )}
          {!loading && rows.map((r) => (
            <div
              key={r.id}
              className="rounded-md border border-slate-200 px-3 py-2 hover:bg-slate-50"
              data-testid={`cfo-signal-${r.id}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="font-semibold text-slate-800">{r.type}</span>
                <span className="flex items-center gap-1">
                  {r.resolved && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium" data-testid={`signal-resolved-${r.id}`}>
                      ✓ تم الحل
                    </span>
                  )}
                  <span className={r.resolved ? 'text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 line-through' : sevPill(r.severity)}>{r.severity}</span>
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {formatTime(r.emitted_at)}{r.period && <> · {r.period}</>}{r.source && <> · {r.source}</>}
              </p>
              {r.value !== null && r.value !== undefined && (
                <p className="text-[11px] text-slate-700 mt-1">
                  Value: <span className="font-mono">{r.value}</span>
                </p>
              )}
              {r.details && Object.keys(r.details).length > 0 && (
                <details className="mt-1">
                  <summary className="text-[10px] text-slate-500 cursor-pointer">details</summary>
                  <pre className="mt-1 text-[10px] bg-slate-50 rounded p-2 overflow-x-auto">
                    {JSON.stringify(r.details, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
